import { apiClient } from '@/lib/api';

/** Las secciones de Mi empresa, con su dirección (`/mi-empresa/<seccion>`). */
export type SeccionDeMiEmpresa =
  | 'resumen'
  | 'usuarios'
  | 'roles'
  | 'equipos'
  | 'aprobaciones'
  | 'documentos'
  | 'pagos';

export interface PendienteDeConfiguracion {
  clave: string;
  texto: string;
  accion: string;
  seccion: SeccionDeMiEmpresa;
}

export interface ResumenDeMiEmpresa {
  empresa: { nombre: string; nit: string | null; pais: string | null };
  /** 0–100: cuánto de la empresa está configurado. */
  configurada: number;
  pendientes: PendienteDeConfiguracion[];
  usuarios: { total: number; activos: number; invitaciones: number };
  roles: { total: number; nombres: string[] };
  equipos: { total: number; resumen: string };
  aprobaciones: { reglas: number; tipos: string[] };
  documentos: { revisada: boolean };
}

export type EstadoDePersona = 'ACTIVO' | 'INVITADO' | 'INVITACION_VENCIDA' | 'INACTIVO';

export interface PersonaDeLaEmpresa {
  id: string;
  email: string;
  nombre: string;
  apellido: string;
  rol: 'COMPANY_ADMIN' | 'SALESPERSON' | 'APPROVER' | 'PLATFORM_ADMIN';
  /** El nombre que se ve: su rol personalizado o el rol base. */
  cargo: string;
  customRoleId: string | null;
  equipos: Array<{ id: string; name: string }>;
  estado: EstadoDePersona;
  ultimoAcceso: string | null;
}

export interface Invitacion {
  email: string;
  nombre: string;
  apellido?: string;
  customRoleId?: string | null;
  rol?: 'COMPANY_ADMIN' | 'SALESPERSON' | 'APPROVER';
  equipoId?: string | null;
}

export interface EquipoDelOrganigrama {
  id: string;
  nombre: string;
  codigo: string;
  descripcion: string | null;
  parentId: string | null;
  lider: { id: string; firstName: string; lastName: string } | null;
  miembros: Array<{ id: string; firstName: string; lastName: string }>;
}

export interface Facturacion {
  plan: { nombre: string; tier: string; precioMensual: number; moneda: string } | null;
  esPrueba: boolean;
  proximoCobro: string | null;
  diasParaVencer: number | null;
  /** Sólo si la plataforma los cargó: la pantalla no inventa una cuenta. */
  transferencia: { banco: string; cuenta: string; titular: string } | null;
  qrUrl: string | null;
}

export type PedidoDeFacturacion = 'CAMBIAR_TARJETA' | 'AGREGAR_TARJETA' | 'QR' | 'TRANSFERENCIA';

export const miEmpresaService = {
  resumen: (): Promise<ResumenDeMiEmpresa> => apiClient.get('/api/mi-empresa/resumen'),
  personas: (): Promise<PersonaDeLaEmpresa[]> => apiClient.get('/api/mi-empresa/usuarios'),
  /** Sólo en una empresa demo: cuántas personas lleva y hasta cuántas puede tener. Si no, `null`. */
  cupoDePersonas: (): Promise<{ tope: number; usadas: number } | null> =>
    apiClient.get('/api/mi-empresa/usuarios/cupo'),
  invitar: (datos: Invitacion): Promise<{ id: string; correoEnviado: boolean }> =>
    apiClient.post('/api/mi-empresa/invitar', datos),
  reenviar: (id: string): Promise<{ reenviada: boolean }> =>
    apiClient.post(`/api/mi-empresa/usuarios/${id}/reenviar`, {}),
  organigrama: (): Promise<EquipoDelOrganigrama[]> => apiClient.get('/api/mi-empresa/organigrama'),
  jerarquia: (id: string, cambio: { parentId?: string | null; leaderId?: string | null }) =>
    apiClient.put(`/api/mi-empresa/equipos/${id}/jerarquia`, cambio),
  /** Cotizaciones esperando firma, por regla de aprobación. */
  pendientesPorRegla: (): Promise<Record<string, number>> =>
    apiClient.get('/api/mi-empresa/aprobaciones/pendientes'),
  facturacion: (): Promise<Facturacion> => apiClient.get('/api/mi-empresa/facturacion'),
  /** Le llega por correo a quien cobra: Prixia no guarda datos de tarjetas. */
  pedir: (tipo: PedidoDeFacturacion): Promise<{ avisados: number }> =>
    apiClient.post('/api/mi-empresa/facturacion/pedido', { tipo }),
};
